// EventDetailsModal.js (responsive version with Lab Filtering)
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const EventDetailsModal = ({ 
  isOpen, 
  onClose, 
  eventData, 
  selectedSection = 'Cases',
  filteredCases = [],  // Added: Receive filtered cases from Dashboard
  userType = 'admin',  // Added: Receive user type
  labId = null         // Added: Receive lab ID
}) => {
  const [activeTab, setActiveTab] = useState('Cases');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen && eventData) {
      // Set the active tab based on which section was clicked
      if (selectedSection === 'Arrive' || selectedSection === 'New Cases') {
        setActiveTab('Cases');
      } else if (selectedSection === 'Tasks') {
        setActiveTab('Tasks');
      } else if (selectedSection === 'Ship' || selectedSection === 'Deliveries') {
        setActiveTab('Shipping');
      } else {
        setActiveTab('Cases');
      }

      // Filter cases for the selected date
      if (eventData.fullDate && filteredCases.length > 0) {
        const dateString = formatDateToYYYYMMDD(eventData.fullDate);
        const casesForDate = getCasesForDate(dateString, selectedSection);
        setFilteredData(casesForDate);
        
        console.log('EventDetailsModal: Filtered data for', dateString, casesForDate.length, 'cases');
      }
    }
  }, [isOpen, eventData, selectedSection, filteredCases]);

  // Helper function to format date to YYYY-MM-DD
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // Function to filter cases for the selected date and section
  const getCasesForDate = (dateString, section) => {
    if (!filteredCases || filteredCases.length === 0) return [];
    
    let filtered = [];
    
    switch(section) {
      case 'New Cases':
        filtered = filteredCases.filter(c => {
          const caseDate = c.date || c.start_date;
          if (!caseDate) return false;
          const caseDateString = formatDateToYYYYMMDD(caseDate);
          return caseDateString === dateString;
        });
        break;
        
      case 'Arrive':
        filtered = filteredCases.filter(c => {
          if (!c.arrive_date) return false;
          const arriveDateString = formatDateToYYYYMMDD(c.arrive_date);
          return arriveDateString === dateString;
        });
        break;
        
      case 'Ship':
        filtered = filteredCases.filter(c => {
          if (!c.delivery_date) return false;
          const shipDateString = formatDateToYYYYMMDD(c.delivery_date);
          return shipDateString === dateString;
        });
        break;
        
      case 'Deliveries':
        filtered = filteredCases.filter(c => {
          if (!c.delivery_date) return false;
          const deliveryDateString = formatDateToYYYYMMDD(c.delivery_date);
          return deliveryDateString === dateString;
        });
        break;
        
      case 'Day Overview':
        // For Day Overview, show all cases for that date
        filtered = filteredCases.filter(c => {
          const caseDate = c.date || c.start_date;
          const arriveDate = c.arrive_date;
          const deliveryDate = c.delivery_date;
          
          const caseDateString = caseDate ? formatDateToYYYYMMDD(caseDate) : '';
          const arriveDateString = arriveDate ? formatDateToYYYYMMDD(arriveDate) : '';
          const deliveryDateString = deliveryDate ? formatDateToYYYYMMDD(deliveryDate) : '';
          
          return caseDateString === dateString || 
                 arriveDateString === dateString || 
                 deliveryDateString === dateString;
        });
        break;
        
      default:
        // Default: show all cases for the date
        filtered = filteredCases.filter(c => {
          const caseDate = c.date || c.start_date;
          if (!caseDate) return false;
          const caseDateString = formatDateToYYYYMMDD(caseDate);
          return caseDateString === dateString;
        });
    }
    
    return filtered;
  };

  // Function to get cases arriving for the date
  const getArrivingCases = (dateString) => {
    return filteredCases.filter(c => {
      if (!c.arrive_date) return false;
      const arriveDateString = formatDateToYYYYMMDD(c.arrive_date);
      return arriveDateString === dateString;
    });
  };

  // Function to get cases shipping for the date
  const getShippingCases = (dateString) => {
    return filteredCases.filter(c => {
      if (!c.delivery_date) return false;
      const shipDateString = formatDateToYYYYMMDD(c.delivery_date);
      return shipDateString === dateString;
    });
  };

  // Function to get new cases for the date
  const getNewCases = (dateString) => {
    return filteredCases.filter(c => {
      const caseDate = c.date || c.start_date;
      if (!caseDate) return false;
      const caseDateString = formatDateToYYYYMMDD(caseDate);
      return caseDateString === dateString;
    });
  };

  if (!isOpen || !eventData) return null;

  const tabs = ['Cases', 'Activity', 'Tasks', 'Shipping'];
  const dateString = formatDateToYYYYMMDD(eventData.fullDate);
  
  // Get data for each section
  const newCases = getNewCases(dateString);
  const arrivingCases = getArrivingCases(dateString);
  const shippingCases = getShippingCases(dateString);
  const allCasesForDay = getCasesForDate(dateString, 'Day Overview');

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: isMobile ? '0.5rem' : '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: isMobile ? '95%' : '60%',
        maxWidth: isMobile ? '100%' : '500px',
        maxHeight: isMobile ? '80vh' : '52vh',
        overflow: 'hidden',
        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Blue Header */}
        <div style={{
          backgroundColor: '#004aad',
          color: 'white',
          padding: isMobile ? '12px 14px' : '14px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: '8px 8px 0 0'
        }}>
          <div>
            <h2 style={{ 
              margin: 0, 
              fontSize: isMobile ? '1.2rem' : '1.5rem', 
              fontWeight: '600' 
            }}>
              {eventData.date}
            </h2>
            {userType === 'lab' && labId && (
              <div style={{
                fontSize: '0.7rem',
                opacity: 0.8,
                marginTop: '2px'
              }}>
            
              </div>
            )}
            {userType === 'admin' && (
              <div style={{
                fontSize: '0.7rem',
                opacity: 0.8,
                marginTop: '2px'
              }}>
                Showing {filteredData.length} cases
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '6px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={isMobile ? 24 : 28} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          overflowX: 'auto'
        }}>
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: isMobile ? '0.75rem 1rem' : '1rem 2rem',
                fontSize: isMobile ? '0.9rem' : '1rem',
                fontWeight: activeTab === tab ? '600' : '500',
                color: activeTab === tab ? '#333' : '#004aad',
                backgroundColor: activeTab === tab ? 'white' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                minWidth: isMobile ? '80px' : 'auto',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab) e.currentTarget.style.backgroundColor = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        <div style={{
          padding: isMobile ? '1rem' : '2rem',
          overflow: 'auto',
          flex: 1,
        }}>
          {activeTab === 'Cases' && (
            <>
              {/* New Cases */}
              <div style={{ marginBottom: isMobile ? '1rem' : '1.5rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.75rem'
                }}>
                  <h3 style={{
                    margin: 0,
                    color: '#333',
                    fontSize: isMobile ? '1rem' : '1.1rem',
                    fontWeight: 'bold'
                  }}>
                    New Cases ({newCases.length})
                  </h3>
                  {newCases.length > 0 && (
                    <span style={{
                      fontSize: '0.8rem',
                      color: '#64748b',
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {selectedSection === 'New Cases' ? 'Selected' : ''}
                    </span>
                  )}
                </div>
                
                {newCases.length === 0 ? (
                  <div style={{
                    color: '#94a3b8',
                    fontStyle: 'italic',
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    padding: '0.5rem 0'
                  }}>
                    No new cases for {eventData.date}
                  </div>
                ) : (
                  <ul style={{
                    margin: 0,
                    paddingLeft: '1.5rem',
                    color: '#334155',
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    lineHeight: '1.6',
                    maxHeight: '150px',
                    overflowY: 'auto'
                  }}>
                    {newCases.slice(0, 10).map((caseItem, index) => (
                      <li key={index}>
                        Case <span style={{color: '#004aad', fontWeight: 'bold'}}>
                          #{caseItem.case_id || `C${index + 1}`}
                        </span>
                        {caseItem.patient_name && ` - ${caseItem.patient_name}`}
                        {caseItem.last_name && ` - ${caseItem.last_name}`}
                        {caseItem.status && ` (${caseItem.status})`}
                      </li>
                    ))}
                    {newCases.length > 10 && (
                      <li style={{ color: '#64748b', fontStyle: 'italic' }}>
                        ...and {newCases.length - 10} more cases
                      </li>
                    )}
                  </ul>
                )}
              </div>

              {/* Cases Arriving */}
              <div style={{
               padding: '0.5rem 0',
               marginBottom: isMobile ? '0.75rem' : '1rem',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <h3 style={{
                    margin: 0,
                    color: '#333',
                    fontSize: isMobile ? '1rem' : '1.1rem',
                    fontWeight: 'bold'
                  }}>
                    Cases Arriving ({arrivingCases.length})
                  </h3>
                  {arrivingCases.length > 0 && (
                    <span style={{
                      fontSize: '0.8rem',
                      color: '#64748b',
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {selectedSection === 'Arrive' ? 'Selected' : ''}
                    </span>
                  )}
                </div>
                
                {arrivingCases.length === 0 ? (
                  <div style={{
                    color: '#94a3b8',
                    fontStyle: 'italic',
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    padding: '0.5rem 0'
                  }}>
                    No cases arriving on {eventData.date}
                  </div>
                ) : (
                  <ul style={{
                    margin: 0,
                    paddingLeft: '1.5rem',
                    color: '#333',
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    lineHeight: '1.5',
                    maxHeight: '150px',
                    overflowY: 'auto'
                  }}>
                    {arrivingCases.slice(0, 10).map((caseItem, index) => (
                      <li key={index}>
                        Case <span style={{color: '#004aad', fontWeight: 'bold'}}>
                          #{caseItem.case_id || `C${index + 1}`}
                        </span>
                        {caseItem.patient_name && ` - ${caseItem.patient_name}`}
                        {caseItem.last_name && ` - ${caseItem.last_name}`}
                        {caseItem.status && ` (${caseItem.status})`}
                      </li>
                    ))}
                    {arrivingCases.length > 10 && (
                      <li style={{ color: '#64748b', fontStyle: 'italic' }}>
                        ...and {arrivingCases.length - 10} more cases
                      </li>
                    )}
                  </ul>
                )}
              </div>

              {/* Cases Shipping */}
              <div style={{
                padding: '0.5rem 0',
                marginBottom: isMobile ? '0.75rem' : '1rem',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <h3 style={{
                    margin: 0,
                    color: '#333',
                    fontSize: isMobile ? '1rem' : '1.1rem',
                    fontWeight: 'bold'
                  }}>
                    Cases Shipping ({shippingCases.length})
                  </h3>
                  {shippingCases.length > 0 && (
                    <span style={{
                      fontSize: '0.8rem',
                      color: '#64748b',
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {selectedSection === 'Ship' ? 'Selected' : ''}
                    </span>
                  )}
                </div>
                
                {shippingCases.length === 0 ? (
                  <div style={{
                    color: '#94a3b8',
                    fontStyle: 'italic',
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    padding: '0.5rem 0'
                  }}>
                    No cases shipping on {eventData.date}
                  </div>
                ) : (
                  <ul style={{
                    margin: 0,
                    paddingLeft: '1.5rem',
                    color: '#333',
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    lineHeight: '1.5',
                    maxHeight: '150px',
                    overflowY: 'auto'
                  }}>
                    {shippingCases.slice(0, 10).map((caseItem, index) => (
                      <li key={index}>
                        Case <span style={{color: '#004aad', fontWeight: 'bold'}}>
                          #{caseItem.case_id || `C${index + 1}`}
                        </span>
                        {caseItem.patient_name && ` - ${caseItem.patient_name}`}
                        {caseItem.last_name && ` - ${caseItem.last_name}`}
                        {caseItem.status && ` (${caseItem.status})`}
                      </li>
                    ))}
                    {shippingCases.length > 10 && (
                      <li style={{ color: '#64748b', fontStyle: 'italic' }}>
                        ...and {shippingCases.length - 10} more cases
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </>
          )}

          {activeTab === 'Activity' && (
            <div style={{
              textAlign: 'center',
              color: '#94a3b8',
              fontStyle: 'italic',
              padding: isMobile ? '3rem 0' : '4rem 0'
            }}>
              {allCasesForDay.length === 0 ? (
                `No activity for ${eventData.date}`
              ) : (
                <div>
                  <div style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>
                    📊 Daily Activity Summary
                  </div>
                  <div style={{ textAlign: 'left', maxWidth: '300px', margin: '0 auto' }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>New Cases:</strong> {newCases.length}
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Cases Arriving:</strong> {arrivingCases.length}
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Cases Shipping:</strong> {shippingCases.length}
                    </div>
                    <div>
                      <strong>Total Cases Today:</strong> {allCasesForDay.length}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'Tasks' && (
            <div style={{
              textAlign: 'center',
              color: '#94a3b8',
              fontStyle: 'italic',
              padding: isMobile ? '3rem 0' : '4rem 0'
            }}>
              {allCasesForDay.length === 0 ? (
                `No tasks for ${eventData.date}`
              ) : (
                <div>
                  <div style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>
                    📋 Pending Tasks
                  </div>
                  <div style={{ textAlign: 'left', maxWidth: '300px', margin: '0 auto' }}>
                    {newCases.length > 0 && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong>Review New Cases:</strong> {newCases.length} case(s)
                      </div>
                    )}
                    {arrivingCases.length > 0 && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong>Prepare for Arrival:</strong> {arrivingCases.length} case(s)
                      </div>
                    )}
                    {shippingCases.length > 0 && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong>Prepare for Shipping:</strong> {shippingCases.length} case(s)
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'Shipping' && (
            <>
              {/* Cases Shipping Section */}
              <div style={{ marginBottom: isMobile ? '1.5rem' : '2rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <h3 style={{
                    margin: 0,
                    color: '#333',
                    fontSize: isMobile ? '1rem' : '1.1rem',
                    fontWeight: 'bold'
                  }}>
                    Cases Shipping ({shippingCases.length})
                  </h3>
                  {shippingCases.length > 0 && (
                    <span style={{
                      fontSize: '0.8rem',
                      color: '#64748b',
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {selectedSection === 'Ship' || selectedSection === 'Deliveries' ? 'Selected' : ''}
                    </span>
                  )}
                </div>
                
                {shippingCases.length === 0 ? (
                  <div style={{
                    color: '#94a3b8',
                    fontStyle: 'italic',
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    padding: '0.5rem 0'
                  }}>
                    No cases shipping on {eventData.date}
                  </div>
                ) : (
                  <ul style={{
                    margin: 0,
                    paddingLeft: '1.5rem',
                    color: '#333',
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    lineHeight: '1.8',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {shippingCases.slice(0, 15).map((caseItem, index) => (
                      <li key={index}>
                        Case <span style={{color: '#004aad', fontWeight: 'bold'}}>
                          #{caseItem.case_id || `C${index + 1}`}
                        </span>
                        {caseItem.patient_name && ` - ${caseItem.patient_name}`}
                        {caseItem.last_name && ` - ${caseItem.last_name}`}
                        {caseItem.delivery_address && ` → ${caseItem.delivery_address.substring(0, 20)}...`}
                        {caseItem.status && ` (${caseItem.status})`}
                      </li>
                    ))}
                    {shippingCases.length > 15 && (
                      <li style={{ color: '#64748b', fontStyle: 'italic' }}>
                        ...and {shippingCases.length - 15} more cases
                      </li>
                    )}
                  </ul>
                )}
              </div>

              {/* Deliveries Summary */}
              {shippingCases.length > 0 && (
                <div style={{
                  backgroundColor: '#f8fafc',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginTop: '1rem'
                }}>
                  <h4 style={{
                    margin: '0 0 0.5rem 0',
                    color: '#334155',
                    fontSize: isMobile ? '0.9rem' : '1rem'
                  }}>
                    📦 Delivery Summary
                  </h4>
                  <div style={{ fontSize: isMobile ? '0.85rem' : '0.9rem' }}>
                    <div style={{ marginBottom: '0.25rem' }}>
                      • Total deliveries: <strong>{shippingCases.length}</strong>
                    </div>
                    <div style={{ marginBottom: '0.25rem' }}>
                      • Cases ready for pickup: <strong>{shippingCases.filter(c => c.status === 'ready').length}</strong>
                    </div>
                    <div>
                      • Cases in transit: <strong>{shippingCases.filter(c => c.status === 'in_transit').length}</strong>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetailsModal;